import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { mx } from "../lib/theme";

export function MxHairline({ dark = false, sx }) {
  return <Box sx={{ height: "1px", bgcolor: dark ? mx.hairlineDark : mx.hairline, ...sx }} />;
}

MxHairline.propTypes = {
  dark: PropTypes.bool,
  sx: PropTypes.object,
};

MxHairline.defaultProps = {
  dark: false,
  sx: null,
};

export function MxEyebrow({ children, dark = false }) {
  return (
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: 800,
        letterSpacing: 1.15,
        textTransform: "uppercase",
        color: dark ? "rgba(255,255,255,0.55)" : mx.accent,
      }}
    >
      {children}
    </Typography>
  );
}

MxEyebrow.propTypes = {
  children: PropTypes.node.isRequired,
  dark: PropTypes.bool,
};

MxEyebrow.defaultProps = {
  dark: false,
};

export function MxLiftCard({ children, sx }) {
  const { p, padding, ...restSx } = sx || {};
  return (
    <Box
      sx={{
        bgcolor: mx.surface,
        borderRadius: "4px",
        boxShadow: mx.liftShadow,
        overflow: "hidden",
        ...restSx,
      }}
    >
      <Box sx={{ height: 4, background: mx.swoosh }} />
      <Box sx={{ p: p ?? padding ?? 2 }}>{children}</Box>
    </Box>
  );
}

MxLiftCard.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

MxLiftCard.defaultProps = {
  sx: null,
};

export function MxDarkBand({ children, sx }) {
  return (
    <Box sx={{ bgcolor: mx.ink, color: "#fff", px: 2, py: 1.75, ...sx }}>
      {children}
    </Box>
  );
}

MxDarkBand.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

MxDarkBand.defaultProps = {
  sx: null,
};

export function MxBtn({ children, onClick, fullWidth = false, variant = "solid", light = false }) {
  const outline = variant === "outline";
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: fullWidth ? "100%" : "auto",
        px: 2,
        py: 1.25,
        borderRadius: "3px",
        bgcolor: outline ? "transparent" : light ? "#fff" : mx.accent,
        color: outline ? (light ? "#fff" : mx.ink) : light ? mx.ink : "#fff",
        border: outline ? `1px solid ${light ? "rgba(255,255,255,0.35)" : mx.hairline}` : "none",
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: 0.1,
      }}
    >
      {children}
    </ButtonBase>
  );
}

MxBtn.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(["solid", "outline"]),
  light: PropTypes.bool,
};

MxBtn.defaultProps = {
  fullWidth: false,
  variant: "solid",
  light: false,
};

/** Full-width primary row — ink bar + arrow. */
export function MxPrimaryAction({ children, onClick, fullWidth = true }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? undefined : 180,
        bgcolor: mx.accent,
        color: "#fff",
        px: 1.75,
        py: 1.35,
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        boxShadow: "0 2px 0 rgba(0,0,0,0.12)",
        "&:hover": { bgcolor: mx.accentHover },
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.1 }}>{children}</Typography>
      <Icon sx={{ fontSize: 22 }}>arrow_forward</Icon>
    </ButtonBase>
  );
}

MxPrimaryAction.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
};

MxPrimaryAction.defaultProps = {
  fullWidth: true,
};

/** Secondary row — hairline divider + corner arrow. */
export function MxSecondaryAction({ children, onClick, fullWidth = true }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? undefined : 160,
        px: 0.25,
        py: 1.15,
        mt: fullWidth ? 0.5 : 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${mx.hairline}`,
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 650, color: mx.ink }}>{children}</Typography>
      <Icon sx={{ fontSize: 22, color: mx.subtle }}>north_east</Icon>
    </ButtonBase>
  );
}

MxSecondaryAction.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
};

MxSecondaryAction.defaultProps = {
  fullWidth: true,
};

export function MxTextLink({ label, onClick, light = false }) {
  return (
    <ButtonBase onClick={onClick} sx={{ py: 0.25 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 650,
          color: light ? "#fff" : mx.ink,
          borderBottom: `1px solid ${light ? "rgba(255,255,255,0.55)" : mx.accent}`,
          pb: 0.15,
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

MxTextLink.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  light: PropTypes.bool,
};

MxTextLink.defaultProps = {
  light: false,
};

export function MxSectionHead({ eyebrow, title, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 1.5 }}>
      <Box>
        {eyebrow ? <MxEyebrow>{eyebrow}</MxEyebrow> : null}
        {title ? (
          <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.35, lineHeight: 1.1, mt: eyebrow ? 0.45 : 0, color: mx.ink }}>
            {title}
          </Typography>
        ) : null}
      </Box>
      {action || null}
    </Stack>
  );
}

MxSectionHead.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  action: PropTypes.node,
};

MxSectionHead.defaultProps = {
  eyebrow: "",
  title: "",
  action: null,
};

export function MxFilterPill({ active, label, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 1.25,
        py: 0.65,
        borderRadius: "999px",
        bgcolor: active ? mx.ink : "transparent",
        color: active ? "#fff" : mx.ink,
        border: active ? "none" : `1px solid ${mx.hairline}`,
        fontSize: 13,
        fontWeight: 650,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </ButtonBase>
  );
}

MxFilterPill.propTypes = {
  active: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};
